import { api, type ApiError } from '../api/client.js';
import { copyTextWithFeedback } from '../util/copy-feedback.js';

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Owner-only manual publish-token rotation. Two phases in one dialog:
 * a warning + confirm (the old token dies immediately; every configured
 * sa-tools project must be re-configured), then the show-once readout of
 * the new token with a copy button — the same UX as component creation.
 * Resolves `true` when a rotation actually happened.
 */
export function openRotateTokenModal(input: {
  componentId: string;
  componentName: string;
}): Promise<boolean> {
  return new Promise((resolve) => {
    const dialog = document.createElement('sl-dialog');
    dialog.label = 'Rotate publish token';
    dialog.innerHTML = `
      <div id="rt-confirm" class="field-stack">
        <sl-alert variant="warning" open>
          <sl-icon slot="icon" name="exclamation-triangle"></sl-icon>
          <strong>The current token stops working immediately.</strong>
          Every project configured to publish
          <code>${escapeHtml(input.componentName)}</code> will get a 401 until
          you paste the new token into its sa-tools <em>Configure CDN</em> prompt.
        </sl-alert>
        <sl-alert id="rt-error" variant="danger" closable></sl-alert>
      </div>

      <div id="rt-result" style="display:none">
        <sl-alert variant="success" open>
          <sl-icon slot="icon" name="check2-circle"></sl-icon>
          Token rotated. Copy it now and update your sa-tools projects.
        </sl-alert>
        <div class="field-stack" style="margin-top:0.75rem">
          <label><strong>New publish token</strong></label>
          <div class="token-readout">
            <code id="rt-token-text"></code>
            <sl-icon-button id="rt-copy" class="copy-feedback" name="clipboard" label="Copy token"></sl-icon-button>
          </div>
          <small>This is shown only once. Store it somewhere safe.</small>
        </div>
      </div>

      <sl-button slot="footer" id="rt-cancel" variant="default">Cancel</sl-button>
      <sl-button slot="footer" id="rt-rotate" variant="warning">Rotate token</sl-button>
      <sl-button slot="footer" id="rt-done" variant="primary" style="display:none">Done</sl-button>
    `;

    document.body.appendChild(dialog);

    const confirmEl = dialog.querySelector<HTMLElement>('#rt-confirm')!;
    const resultEl = dialog.querySelector<HTMLElement>('#rt-result')!;
    const errorEl = dialog.querySelector<HTMLElement & { open: boolean }>('#rt-error')!;
    const tokenText = dialog.querySelector<HTMLElement>('#rt-token-text')!;
    const cancelBtn = dialog.querySelector<HTMLElement>('#rt-cancel')!;
    const rotateBtn = dialog.querySelector<HTMLElement>('#rt-rotate')!;
    const doneBtn = dialog.querySelector<HTMLElement>('#rt-done')!;
    const copyBtn = dialog.querySelector<HTMLElement>('#rt-copy')!;

    let rotated = false;
    let plaintext = '';

    cancelBtn.addEventListener('click', () => dialog.hide());
    doneBtn.addEventListener('click', () => dialog.hide());
    dialog.addEventListener('sl-after-hide', () => {
      dialog.remove();
      resolve(rotated);
    });

    rotateBtn.addEventListener('click', async () => {
      errorEl.open = false;
      const loadingBtn = rotateBtn as HTMLButtonElement & { loading: boolean };
      try {
        loadingBtn.loading = true;
        const res = await api.rotateComponentToken(input.componentId);
        rotated = true;
        plaintext = res.token;
        tokenText.textContent = res.token;

        confirmEl.style.display = 'none';
        resultEl.style.display = '';
        rotateBtn.style.display = 'none';
        cancelBtn.style.display = 'none';
        doneBtn.style.display = '';
      } catch (err) {
        console.error('[rotate-token] failed', err);
        const apiErr = err as ApiError;
        errorEl.textContent = apiErr?.message ?? 'Failed to rotate token';
        errorEl.open = true;
      } finally {
        loadingBtn.loading = false;
      }
    });

    copyBtn.addEventListener('click', async () => {
      if (!plaintext) return;
      await copyTextWithFeedback(plaintext, copyBtn, { copiedLabel: 'Copied token' });
    });

    requestAnimationFrame(() => {
      (dialog as HTMLElement & { show: () => void }).show();
    });
  });
}
